/* eslint-disable import/no-import-module-exports */
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Router } from 'react-router-dom';
import {
  init, reactRouterV5Instrumentation, ErrorBoundary as SentryErrorBoundary, BrowserTracing,
} from '@sentry/react';
import { LicenseInfo } from '@mui/x-license-pro';
import {
  ThemeProvider, CssBaseline,
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { getStore, persistor, history } from '@client/store';
import '../css/app.css';
import './serviceWorker';
import { CustomTheme } from '@client/theme';
import { Modal } from '@client/components/globals/Modal';
import App from './components/App';
import { ErrorBoundary } from './components/_helpers/ErrorBoundary';

LicenseInfo.setLicenseKey(process.env.MUI_X_LICENSE_KEY);

if (process.env.NODE_ENV !== 'development') {
  init({
    dsn: process.env.SENTRY_DSN,
    normalizeDepth: 10,
    integrations: [new BrowserTracing({
      routingInstrumentation: reactRouterV5Instrumentation(history),
    })],
  });
} else {
  /**
   * Silence emotion errors for :first-child SSR
   * Emotion emits this error regardless of whether or not SSR is being used
   * https://github.com/emotion-js/emotion/issues/1105#issuecomment-1885044724
   */
  const consoleError = console.error;

  console.error = function filterErrors(msg, ...args) {
    if (/server-side rendering/.test(msg)) {
      return;
    }
    consoleError(msg, ...args);
  };
}

history.listen(() => {
  Modal.hide();
});

const store = getStore();

const container = document.getElementById('app');
const root = createRoot(container);

root.render(
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={CustomTheme}>
      <CssBaseline />

      <SentryErrorBoundary fallback={<ErrorBoundary />}>
        <Provider store={store}>
          <PersistGate
            persistor={persistor}
            loading={null}
          >
            <Router history={history}>
              <App />
            </Router>
          </PersistGate>
        </Provider>
      </SentryErrorBoundary>
    </ThemeProvider>
  </StyledEngineProvider>,
);

if (module.hot) {
  module.hot.accept();
}
